import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card } from '@icetus/ui';
import {
  ApiError,
  changePassword,
  fetchSessions,
  revokeOtherSessions,
  revokeSession,
  updateProfile,
  type LoginSession,
  type MeResponse,
} from '../api';
import { useConfirm } from '../components/confirm';
import { Toaster, useToasts } from '../components/toast';
import { phoneTypingValue } from '../lib/phone-input';

/**
 * 个人资料。
 *
 * 这是控制台里**唯一不跟租户走**的页面：同一个人可能在三个客户的站点里
 * 分别是 owner / editor / viewer，但他的名字和手机号只有一个。
 *
 * 三件事刻意做得不一样：
 *
 * 1) **邮箱只读**。换邮箱要先验证新地址，而验证通过之前新地址不能当登录名 ——
 *    这条链路还没做，所以这里不假装能改，直接写清楚要找我们。
 * 2) **手机号不做验证**。它不是登录凭据，也没有短信通道。界面上必须写明
 *    "不会收到验证码"，否则客户会等一条永远不会来的短信。
 * 3) **改密码保留当前设备**。服务端踢的是其他设备；这一页不能提示
 *    "请重新登录"，那会让客户以为刚改完就掉线了，而其实还能接着用。
 */

/**
 * 两块暂时不露给客户看：
 *
 * - 「登录设备」：现在几乎所有客户都是单人使用，一进来就看到一张 IP + 设备名的表，
 *   先想到的是"是不是有人在盗我的号"，而不是"我能管多少个会话"。
 * - 「忘记密码了怎么办」：重置链路完全走登录页那一侧，这里只是三段说明文字，
 *   作用等于给一个还没遇到的问题提前写答案。
 *
 * 两块都是**开关式隐藏**，不是删除：逻辑与样式都还在，把值改回 true 就回来。
 */
const SESSIONS_ENABLED = false;
const PASSWORD_HELP_ENABLED = false;

type MeUser = NonNullable<MeResponse['user']>;

/**
 * 时间统一按**浏览器本地时区**显示。
 *
 * 服务端存的是 UTC（`2026-10-29T06:00:00Z`），直接截字符串会显示成 06:00，
 * 而客户在东八区看到"我早上六点登录的？"—— 他不记得自己六点起过床。
 */
function when(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 「最近活跃」带上"刚刚"。
 *
 * `last_seen_at` 有 5 分钟节流（见仓储的 touchSession），所以客户正在用的
 * 这台设备，时间戳也可能是四分钟前。直接显示精确时间的话，他会觉得自己
 * 明明在操作却显示"4 分钟前"，像是这一行没刷新。
 */
function lastSeen(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 5 * 60_000) return '刚刚';
  return when(iso);
}

export function ProfilePage({
  user,
  onUpdated,
  onSignOut,
}: {
  user: MeUser;
  onUpdated: () => void;
  /** 退掉自己当前这条会话后，要按登出处理：清本地状态并回登录页 */
  onSignOut: () => void;
}) {
  const { items: toasts, push: toast, dismiss: dismissToast } = useToasts();

  // ── 基本信息 ────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState(user.displayName ?? '');
  const [phone, setPhone] = useState(user.phone ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  /**
   * 脏检查比对**服务端现在的值**，不是页面打开时的值。
   *
   * 用打开时的值做基线的话，保存成功之后按钮仍然是亮的（因为 state 变了、
   * 基线没变），客户会以为没保存上，再点一次。
   */
  const profileDirty = displayName !== (user.displayName ?? '') || phone !== (user.phone ?? '');

  async function onSaveProfile() {
    setSavingProfile(true);
    setProfileError(null);
    try {
      const updated = await updateProfile({ displayName, phone });
      // 用服务端回的值回填，而不是用自己敲的那份：手机号会被归一化
      // （去掉空格与连字符），不覆盖的话输入框里会一直显示"已失效"的旧写法
      setDisplayName(updated.displayName ?? '');
      setPhone(updated.phone ?? '');
      onUpdated();
      toast('资料已保存');
    } catch (err) {
      setProfileError(err instanceof ApiError ? err.message : '保存失败');
    } finally {
      setSavingProfile(false);
    }
  }

  // ── 修改密码 ────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordDone, setPasswordDone] = useState(false);

  async function onChangePassword() {
    if (savingPassword) return;
    setPasswordError(null);
    setPasswordDone(false);

    // 只做"两次一致"与一条长度提示。真正的规则在服务端 `passwordProblem`，
    // 在这里复制一份早晚会和它漂移成两套
    if (newPassword.length < 12) {
      setPasswordError('新密码至少 12 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('两次输入的新密码不一致');
      return;
    }

    setSavingPassword(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordDone(true);
      toast('密码已更新');
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : '密码没有改成，请重试');
    } finally {
      setSavingPassword(false);
    }
  }

  // ── 登录设备 ────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<LoginSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  /** 正在退的那一条；'others' 表示"退出其他所有设备" */
  const [busySession, setBusySession] = useState<string | 'others' | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const refreshSessions = useCallback(async () => {
    setSessionsError(null);
    try {
      const res = await fetchSessions();
      setSessions(res.sessions);
    } catch (err) {
      setSessionsError(err instanceof ApiError ? err.message : '读不到登录设备列表');
    }
  }, []);

  useEffect(() => {
    // 这块不显示时连请求也不发：否则每打开一次个人资料就多一次 login_sessions 查询，
    // 拿回来的结果却没有任何地方能看见
    if (!SESSIONS_ENABLED) return;
    void refreshSessions();
  }, [refreshSessions]);

  const others = (sessions ?? []).filter((s) => !s.current);

  async function onRevokeSession(s: LoginSession) {
    if (busySession) return;
    const ok = await confirm({
      title: s.current ? '退出当前这台设备？' : '让这台设备退出登录？',
      message: s.current ? (
        // 退自己当前这条 = 退出登录，不能说得像只是"清掉一条记录"
        <>你现在就会退出登录，需要重新输入邮箱和密码。</>
      ) : (
        <>
          <strong>{s.device.label}</strong>
          {s.ip ? `（${s.ip}）` : ''} 上的登录会立刻失效，那台设备下次操作时要重新登录。
          <br />
          如果这一条不是你在用的，退出它是对的；退出后建议顺手改一次密码。
        </>
      ),
      confirmText: '退出',
      tone: 'danger',
    });
    if (!ok) return;

    setBusySession(s.id);
    setSessionsError(null);
    try {
      const res = await revokeSession(s.id);
      if (res.wasCurrent) {
        /**
         * 服务端已经删了这条会话，但 cookie 还在浏览器里。
         * 不接着走登出的话，页面会停在原处，客户点下一个按钮才 401 ——
         * 看起来像"点了没反应，然后突然掉线"。
         */
        onSignOut();
        return;
      }
      toast('已退出那台设备');
      await refreshSessions();
    } catch (err) {
      setSessionsError(err instanceof ApiError ? err.message : '没有退成功，请重试');
    } finally {
      setBusySession(null);
    }
  }

  async function onRevokeOthers() {
    if (busySession) return;
    const ok = await confirm({
      title: `退出其他 ${others.length} 台设备？`,
      message: (
        <>
          除了当前这台，其余登录全部失效。被退掉的设备下次操作时需要重新登录。
          <br />
          改过密码之后做这一步最划算：它把改密码之前留下的会话一并清掉。
        </>
      ),
      confirmText: '全部退出',
      tone: 'danger',
    });
    if (!ok) return;

    setBusySession('others');
    setSessionsError(null);
    try {
      const res = await revokeOtherSessions();
      // 用服务端返回的条数而不是本地算的：两者不一致时（比如刚好有一台
      // 在别处登出），以服务端为准，免得报一个没发生过的数字
      toast(`已退出 ${res.removed} 台设备`);
      await refreshSessions();
    } catch (err) {
      setSessionsError(err instanceof ApiError ? err.message : '没有退成功，请重试');
    } finally {
      setBusySession(null);
    }
  }

  return (
    <div className="stack">
      <Toaster items={toasts} onDismiss={dismissToast} />

      <Card title="基本信息" actions={<Badge tone="neutral">所有站点通用</Badge>}>
        <label className="form-row">
          <span>登录邮箱</span>
          {/* 只读的框必须给一个"为什么不能改"的出口，否则客户会反复点它。
              版面上一句话都嫌多，挂 title：鼠标停一下就有答案 */}
          <input
            className="field"
            value={user.email}
            readOnly
            disabled
            // 这一栏有两个用途：告诉浏览器"这里才是用户名"，以及别让它去别的
            // 文本框里猜。少了它，紧挨着密码框的手机号会被当成用户名字段填上邮箱
            autoComplete="username"
            title="邮箱就是登录账号。要换邮箱得先验证新地址，请联系我们处理"
          />
        </label>
        {/* 只有"没验证"才给一句：验证过了还写"邮箱已验证"是在报告一件客户早就知道的事，
            而没验证时那半句是功能限制（不能发布、不能绑域名），必须留着 */}
        {user.emailVerified ? null : (
          <p className="error">这个邮箱还没验证，验证前不能发布页面或绑定域名。</p>
        )}

        <label className="form-row">
          <span>显示名</span>
          <input
            className="field"
            placeholder="例如 张伟"
            maxLength={60}
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>

        <label className="form-row">
          <span>手机号</span>
          <input
            className="field"
            placeholder="+8613800138000"
            // 这两句从版面上撤掉了，但不能真丢：客户会等一条永远不会来的验证码，
            // 或者以为自己填的是登录方式。挂在 title 上，鼠标停一下就能看到
            title="只做登记：不会收到验证码，也不是登录凭据"
            // 必须写 tel：不写的时候浏览器按启发式猜，而这个框正好在「修改密码」
            // 卡上面，被猜成"用户名字段"，登录邮箱就被自动填充进来 —— 客户看到的
            // 是"手机号里填着自己的邮箱"，还带着"有未保存的改动"
            autoComplete="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => {
              const next = phoneTypingValue(e.target.value);
              if (next === null) {
                // 不是手机号（典型的：密码管理器把邮箱塞进来）。把 DOM 退回 state，
                // 既不留下一个假的"未保存的改动"，也不会覆盖客户已经填好的号码
                e.target.value = phone;
                return;
              }
              setPhone(next);
            }}
            onBlur={() => {
              // 失焦时把常见写法（空格、连字符、括号）就地抹平，与保存后的
              // 存储格式一致。只在失焦做，输入过程中改光标位置会很难受
              const cleaned = phone.trim();
              if (!cleaned) return;
              const hasPlus = cleaned.startsWith('+');
              const digits = cleaned.replace(/[\s\-.()\u00a0]/g, '').replace(/^\+/, '');
              setPhone((hasPlus ? '+' : '') + digits);
            }}
          />
        </label>

        <div className="toolbar">
          <Button
            variant="primary"
            disabled={savingProfile || !profileDirty}
            onClick={() => void onSaveProfile()}
          >
            {savingProfile ? '保存中…' : '保存资料'}
          </Button>
          {profileDirty ? <span className="muted">有未保存的改动</span> : null}
        </div>
        {profileError ? <p className="error">{profileError}</p> : null}
      </Card>

      <Card title="修改密码">
        <label className="form-row">
          <span>当前密码</span>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            placeholder="先确认是你本人"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </label>

        <label className="form-row">
          <span>新密码</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            placeholder="至少 12 位"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </label>

        <label className="form-row">
          <span>再输一次新密码</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            placeholder="确认没有敲错"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </label>

        <div className="toolbar">
          <Button
            variant="primary"
            disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
            onClick={() => void onChangePassword()}
          >
            {savingPassword ? '提交中…' : '修改密码'}
          </Button>
        </div>
        {passwordDone ? (
          <p className="notice notice--ok">
            密码已更新。其他设备下次操作时需要重新登录，当前这台不受影响。
          </p>
        ) : null}
        {passwordError ? <p className="error">{passwordError}</p> : null}
      </Card>

      {SESSIONS_ENABLED ? (
        <Card
          title="登录设备"
          actions={
            <Button
              variant="ghost"
              size="sm"
              disabled={busySession !== null}
              onClick={() => void refreshSessions()}
            >
              刷新
            </Button>
          }
        >
          <p className="muted">
            这些设备现在仍处于登录状态。看到不认识的一条，先退出它，再改一次密码。
          </p>

          {sessionsError ? <p className="error">{sessionsError}</p> : null}

          {sessions === null ? (
            <p className="muted">正在读取…</p>
          ) : sessions.length === 0 ? (
            <p className="muted">没有有效的登录会话。</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>设备</th>
                  <th>IP</th>
                  <th>登录时间</th>
                  <th>最近活跃</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.device.label}
                      {s.current ? <Badge tone="success">当前</Badge> : null}
                    </td>
                    {/* 不认识的设备靠 IP 与登录时间判断。"未知"而非留空：
                      留空会让人以为是加载失败，而它其实是真的没记到 */}
                    <td>{s.ip ? <code>{s.ip}</code> : <span className="muted">未知</span>}</td>
                    <td>{when(s.createdAt)}</td>
                    <td>{lastSeen(s.lastSeenAt)}</td>
                    <td>
                      <Button
                        variant="subtle"
                        size="sm"
                        disabled={busySession !== null}
                        onClick={() => void onRevokeSession(s)}
                      >
                        {busySession === s.id ? '退出中…' : s.current ? '退出登录' : '退出'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="toolbar">
            <Button
              variant="danger"
              disabled={busySession !== null || others.length === 0}
              onClick={() => void onRevokeOthers()}
            >
              {busySession === 'others'
                ? '退出中…'
                : `退出其他所有设备${others.length > 0 ? `（${others.length}）` : ''}`}
            </Button>
            {others.length === 0 ? (
              <span className="muted">目前只有这一台在登录，没有可退的设备。</span>
            ) : null}
          </div>
          <p className="muted">
            「最近活跃」最多滞后 5 分钟：服务端为了不给数据库制造写热点，把刷新 节流成了 5
            分钟一次。
          </p>
        </Card>
      ) : null}
      {confirmDialog}

      {PASSWORD_HELP_ENABLED ? (
        <Card title="忘记密码了怎么办">
          <p className="muted">
            退出登录后，在登录页点「忘记密码」，填这个邮箱，我们会寄一条 30 分钟内有效的
            重置链接。链接只能设置密码，不需要告诉我们。
          </p>
          <p className="muted">
            收不到信时先看垃圾箱；还是没有，就在登录页重新申请一次，或者联系我们。
          </p>
        </Card>
      ) : null}
    </div>
  );
}
